<template>
    <div class="list-users-page">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t('usersPage.manageUsers') }}</h6>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div class="row">
                <div class="col">
                    <div class="card">
                        <div>
                            <router-link to="/invite-user" class="btn btn-outline-primary m-4">
                                <i class="ai ai-user-add"></i>
                                {{ $t('usersPage.inviteNewUser') }}
                            </router-link>

                            <el-checkbox
                                v-if="user.role === 'admin'"
                                v-model="showDeleted"
                                class="ml-2">
                                {{ $t('usersPage.showDeleted') }}
                            </el-checkbox>

                            <el-popover
                                placement="left"
                                :title="$t('usersPage.filterUsers')"
                                class="float-right m-4"
                                popper-class="users-table-filter"
                                width="350"
                                trigger="click">
                                <base-button slot="reference" type="primary" outline class="filter-btn">
                                    <i class="m-0 ai ai-filter"></i>
                                    <i class="m-0 ai ai-angle-down"></i>
                                </base-button>
                                <el-form :model="filter" label-width="100px">
                                    <el-form-item :label="$t('general.name')">
                                        <el-input v-model="filter.name"></el-input>
                                    </el-form-item>

                                    <el-form-item :label="$t('general.email')">
                                        <el-input v-model="filter.email"></el-input>
                                    </el-form-item>

                                    <el-form-item :label="$tc('general.application', 1)">
                                        <el-select v-model="filter.app" placeholder=" ">
                                            <el-option :label="`-- ${$t('general.all')} --`" :value="null"></el-option>
                                            <el-option
                                                v-for="app in applications"
                                                :key="app.id"
                                                :label="app.name"
                                                :value="app.id"></el-option>
                                        </el-select>
                                    </el-form-item>

                                    <el-form-item label="Hotel">
                                        <el-select v-model="filter.hotel" placeholder=" ">
                                            <el-option :label="`-- ${$t('general.all')} --`" :value="null"></el-option>
                                            <el-option
                                                v-for="hotel in hotels"
                                                :key="hotel.id"
                                                :label="hotel.name"
                                                :value="hotel.id"></el-option>
                                        </el-select>
                                    </el-form-item>
                                </el-form>
                            </el-popover>

                            <el-table class="table-responsive table-flush"
                                      header-row-class-name="thead-light"
                                      :data="filteredData"
                                      :row-class-name="rowClassName"
                                      :empty-text="isLoadingUsers
                                    ? $t('general.loading') + '...'
                                    : 'No Data'"
                                      lazy>

                                <el-table-column :label="$t('general.name')" prop="name" sortable/>

                                <el-table-column :label="$t('general.email')" prop="email" sortable>
                                    <template v-slot="{row}">
                                        {{ row.email }}
                                        <span v-if="!row.email_verified_at"
                                              class="text-danger">({{ $t('usersPage.notVerifiedYet') }})</span>
                                    </template>
                                </el-table-column>

                                <el-table-column :label="$tc('general.application', 2)">
                                    <template v-slot="{row}">
                                        <div class="avatar-group">
                                            <el-tooltip
                                                v-for="app in row.applications"
                                                :key="app.id"
                                                class="item"
                                                effect="dark"
                                                :content="app.name"
                                                v-if="app"
                                                placement="top">
                                                {{ app.name }}
                                                <img
                                                    v-if="app.logo_url"
                                                    :src="app.logo_url"
                                                    :alt="app.name"
                                                    class="avatar rounded-circle bg-white">
                                                <div
                                                    v-else
                                                    class="application-icon text-icon avatar rounded-circle"
                                                    :class="getRandomBackgroundColor">{{ app.name[0] }}
                                                </div>
                                            </el-tooltip>
                                        </div>
                                    </template>
                                </el-table-column>

                                <el-table-column
                                    v-if="showDeleted"
                                    :label="$t('usersPage.status')"
                                    width="110px">
                                    <template v-slot="{row}">
                                        <span v-if="row.deleted_at" class="text-danger">
                                            {{ $t('usersPage.statusDeleted') }}
                                        </span>
                                        <span v-else class="text-success">
                                            {{ $t('usersPage.statusActive') }}
                                        </span>
                                    </template>
                                </el-table-column>

                                <el-table-column
                                    :label="$t('general.actions')"
                                    class-name="actions-column"
                                    width="130px">
                                    <template v-slot="{row}">
                                        <template v-if="!row.deleted_at">
                                            <el-tooltip
                                                v-if="!row.email_verified_at"
                                                class="item"
                                                effect="dark"
                                                :content="$t('usersPage.resendVerificationEmail')"
                                                placement="top">
                                                <button
                                                    type="button"
                                                    @click="resendVerificationEmail(row)">
                                                    <i class="ai ai-send text-default"></i>
                                                </button>
                                            </el-tooltip>
                                            <el-tooltip
                                                v-if="+user.id !== +row.id"
                                                class="item"
                                                effect="dark"
                                                :content="$t('usersPage.editUser')"
                                                placement="top">
                                                <router-link
                                                    :to="'/users/' + row.id"><i class="ai ai-edit text-primary"></i>
                                                </router-link>
                                            </el-tooltip>
                                            <el-tooltip
                                                v-if="+user.id !== +row.id"
                                                class="item"
                                                effect="dark"
                                                :content="$t('usersPage.deleteUser')"
                                                placement="top">
                                                <button type="button" @click="deleteUserAttention(row.id)">
                                                    <i class="ai ai-remove text-danger"></i>
                                                </button>
                                            </el-tooltip>
                                        </template>
                                        <el-tooltip
                                            v-else
                                            class="item"
                                            effect="dark"
                                            :content="$t('usersPage.restoreUser')"
                                            placement="top">
                                            <button type="button" @click="restoreUserAttention(row)">
                                                <i class="el-icon-refresh text-success"></i>
                                            </button>
                                        </el-tooltip>
                                    </template>
                                </el-table-column>
                            </el-table>

                            <!--
                            <div class="card-footer py-4 d-flex justify-content-center">
                                <base-pagination v-model="currentPage"
                                                 :total="pageCount * itemsPerPage"></base-pagination>
                            </div>
                            -->
                        </div>
                    </div>
                </div>

            </div>

        </div>
        <el-dialog
            v-if="user.role === 'admin'"
            :title="$t('general.warning')"
            :visible.sync="showDeleteUserAttentionModal"
            width="550px"
            center>
            <div class="h2 text-center">{{ $t('usersPage.warningQuestion') }}</div>
            <div slot="footer" class="dialog-footer">
                <base-button
                    outline
                    type="secondary"
                    @click="showDeleteUserAttentionModal = false">{{ $t('general.cancel') }}
                </base-button>
                <base-button type="danger" @click="deleteUser">{{ $t('general.delete') }}</base-button>
            </div>
        </el-dialog>
        <el-dialog
            v-if="user.role === 'admin'"
            :title="$t('general.warning')"
            :visible.sync="showRestoreUserDialog"
            width="550px"
            center>
            <div class="h2 text-center">{{ $t('usersPage.restoreWarningQuestion') }}</div>
            <div slot="footer" class="dialog-footer">
                <base-button
                    outline
                    type="secondary"
                    @click="showRestoreUserDialog = false">{{ $t('general.cancel') }}
                </base-button>
                <base-button type="primary" @click="restoreUser">{{ $t('usersPage.restoreUser') }}</base-button>
            </div>
        </el-dialog>
    </div>
</template>
<script>
    import {mapGetters} from 'vuex'
    import {BaseProgress} from '@/components'
    import RandomBackgroundColor from '@/mixins/random-background-color'
    import DeleteUserMixin from '@/mixins/delete-user'
    import {
        Checkbox,
        Dialog,
        Dropdown,
        DropdownItem,
        DropdownMenu,
        Form,
        FormItem,
        Option,
        Pagination,
        Select,
        Table,
        TableColumn,
    } from 'element-ui'

    export default {
        name: 'user-list',
        mixins: [RandomBackgroundColor, DeleteUserMixin],
        components: {
            BaseProgress,
            [Table.name]: Table,
            [TableColumn.name]: TableColumn,
            [Dropdown.name]: Dropdown,
            [DropdownItem.name]: DropdownItem,
            [DropdownMenu.name]: DropdownMenu,
            [Pagination.name]: Pagination,
            [Form.name]: Form,
            [FormItem.name]: FormItem,
            [Select.name]: Select,
            [Option.name]: Option,
            [Dialog.name]: Dialog,
            [Checkbox.name]: Checkbox,
        },
        computed: {
            ...mapGetters({
                user: 'getUser'
            }),
            filteredData: {
                get() {
                    let filteredUsers = JSON.parse(JSON.stringify(this.tableData));

                    // Hide deleted users unless toggle is on
                    if (!this.showDeleted) {
                        filteredUsers = filteredUsers.filter(user => !user.deleted_at);
                    }

                    // Filter by name
                    if (this.filter.name) {
                        filteredUsers = filteredUsers.filter(user => user.name.toLowerCase().includes(this.filter.name.toLowerCase()));
                    }

                    // Filter by email
                    if (this.filter.email) {
                        filteredUsers = filteredUsers.filter(user => user.email.toLowerCase().includes(this.filter.email.toLowerCase()));
                    }

                    // Filter by hotel
                    if (this.filter.hotel) {
                        filteredUsers = filteredUsers.filter(user => user.hotels.map(hotel => +hotel.id).includes(+this.filter.hotel));
                    }

                    // Filter by application
                    if (this.filter.app) {
                        filteredUsers = filteredUsers.filter(user => user.applications.map(app => +app.id).includes(+this.filter.app));
                    }

                    // this.pageCount = Math.ceil(filteredUsers.length / this.itemsPerPage) || 1;

                    /* Pagination START */
                    // const from = (this.currentPage - 1) * this.itemsPerPage;
                    // const to = from + this.itemsPerPage;
                    // filteredUsers = filteredUsers.slice(from, to);
                    /* Pagination END */

                    return filteredUsers;
                }
            },
        },
        data() {
            return {
                hotels: [],
                applications: [],
                // currentPage: 0,
                // itemsPerPage: 10,
                // pageCount: 0,
                isLoadingUsers: true,
                showDeleted: false,
                showRestoreUserDialog: false,
                selectedUserForRestore: null,
                filter: {
                    hotel: null,
                    app: null
                },
                tableData: [],
                tableFields: [
                    {key: 'name', sortable: true},
                    {key: 'email', sortable: true},
                    {
                        key: 'hotels',
                        sortable: true,
                        formatter: (hotels, key, {permissions}) => {
                            const userHotels = [];

                            for (const hotel of hotels) {
                                const perm = permissions.find(p => p.hotel_id === hotel.id && p.permission !== 'no_access');
                                if (perm) {
                                    userHotels.push(hotel.name)
                                }
                            }

                            return userHotels.join(', ')
                        }
                    },
                    {
                        key: 'applications',
                        sortable: true,
                        formatter: (apps, key, {permissions}) => {
                            const userApps = [];

                            for (const app of apps) {
                                const perm = permissions.find(p => p.application_id === app.id && p.permission !== 'no_access');
                                if (perm) {
                                    userApps.push(app.name);
                                }
                            }
                            return userApps.join(', ')
                        }
                    },
                    {key: 'actions', class: 'actions'},
                ]
            }
        },
        methods: {
            resendVerificationEmail(user) {
                this.$axios.post('/api/email/resend', {userId: user.id})
                    .then(() => {
                        this.$notify({
                            title: this.$t('usersPage.verificationEmailSent'),
                        })
                    })
                    .catch(({response}) => {
                        this.$notify({
                            title: response.data.message,
                            type: 'danger',
                        });
                    });
            },
            rowClassName({row}) {
                return row.deleted_at ? 'is-deleted' : '';
            },
            restoreUserAttention(row) {
                this.selectedUserForRestore = row;
                this.showRestoreUserDialog = true;
            },
            restoreUser() {
                const target = this.selectedUserForRestore;
                if (!target) return;

                this.$axios.post('/api/users/restore', {
                    email: target.email,
                    name: target.name,
                    role: target.role,
                })
                    .then(() => {
                        this.$notify({
                            title: this.$t('usersPage.userRestored'),
                        });
                        const row = this.tableData.find(u => u.id === target.id);
                        if (row) {
                            row.deleted_at = null;
                            row.email_verified_at = null;
                        }
                    })
                    .catch(({response}) => {
                        this.$notify({
                            title: response.data.message,
                            type: 'danger',
                        });
                    })
                    .then(() => {
                        this.selectedUserForRestore = null;
                        this.showRestoreUserDialog = false;
                    });
            }
        },
        async created() {
            let users = (await this.$axios.get('/api/users')).data.data;
            this.hotels = (await this.$axios.get('/api/hotels')).data.data;
            this.applications = (await this.$axios.get('/api/applications')).data.data;

            // Load related applications
            users = users.map(user => {
                const relatedApps = user.permissions
                    .filter(perm => perm.permission !== 'no_access')
                    .map(perm => this.applications.find(app => app.id === perm.application_id))
                    .filter(perm => !!perm);

                user.applications = [...new Set(relatedApps)];

                return user;
            });

            // Load related hotels
            users = users.map(user => {
                const relatedHotels = user.permissions
                    .filter(perm => perm.permission !== 'no_access')
                    .map(perm => this.hotels.find(hotel => hotel.id === perm.hotel_id))
                    .filter(perm => !!perm);

                user.hotels = [...new Set(relatedHotels)];

                return user;
            });

            this.tableData = users;
            this.isLoadingUsers = false;
        },
    };
</script>
<style lang="scss">
    .list-users-page {
        .el-table .is-deleted {
            background-color: #fafafa;
            opacity: 0.65;
        }

        .application-icon {
            display: inline-block;
            width: 36px;
            height: 36px;
            object-fit: cover;
            object-position: center center;
            cursor: default;

            &.text-icon {
                color: #fff;
                text-transform: uppercase;
                text-align: center;
                border-radius: 500px;
                line-height: 36px;
                font-size: 24px;
                vertical-align: middle;
            }
        }
    }

    .users-table-filter {
        .el-select {
            width: 100%;
        }
    }
</style>
